import type { JSX, ReactNode } from 'react'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader
} from '../components/ui/card.tsx'
import { cn } from '../lib/cn.ts'

/**
 * How much horizontal room a panel takes.
 *
 * `form` is the default and is a measure cap, not decoration: a column of
 * fields wider than Tailwind's `2xl` container is hard to read and hard to
 * scan back along.
 *
 * `wide` fills the shell's `<main>`, which sets no width of its own. It is for
 * a panel whose content is a table — a fifteen-column report does not fit in a
 * form measure, and a `className` override would hide that from the next
 * screen that needs one. Choose it deliberately; most screens are `form`.
 */
const WIDTHS = {
  form: 'max-w-2xl',
  wide: 'max-w-none'
} as const satisfies Record<string, string>

/**
 * A phone gets the panel's content without the card around it.
 *
 * On a phone the card's edge and padding and the table's own edge took a
 * fifth of the screen's width in nested frames. The page's `p-4` gutter is
 * margin enough there, so the card goes flat below `sm` and keeps every other
 * breakpoint.
 *
 * Only inside `<main>`. A `Dialog` draws no frame of its own and leans on its
 * Panel for one, and it is portalled outside `<main>`. A flat Panel there
 * left the dialog with no fill, and its content floated over the page.
 */
const PHONE_FLAT =
  'max-sm:in-[main]:rounded-none max-sm:in-[main]:border-0 max-sm:in-[main]:bg-transparent max-sm:in-[main]:py-0 max-sm:in-[main]:shadow-none'

/** The card's side padding, dropped with its frame. */
const PHONE_FLUSH = 'max-sm:in-[main]:px-0'

export interface PanelProps {
  'data-testid': string
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  width?: keyof typeof WIDTHS
  className?: string
}

/**
 * Panel — the console's unit of routed content.
 *
 * Every screen is a titled card, so the heading level and spacing are decided
 * once here instead of per screen. Screens compose Panels; they do not reach
 * for `Card` directly.
 *
 * @param props - Panel props.
 * @param props.title - The panel heading.
 * @param props.description - Optional supporting line under the title.
 * @param props.children - Panel body.
 * @param props.width - How much horizontal room to take; see `WIDTHS`.
 * @param props.className - Extra classes.
 * @returns The rendered panel.
 */
export function Panel({
  'data-testid': testId,
  title,
  description,
  children,
  width = 'form',
  className
}: PanelProps): JSX.Element {
  return (
    <Card
      data-testid={testId}
      className={cn(WIDTHS[width], PHONE_FLAT, className)}
    >
      <CardHeader className={PHONE_FLUSH}>
        <h1 className="text-2xl leading-none font-semibold">{title}</h1>
        {description ? (
          <CardDescription className="text-pretty">
            {description}
          </CardDescription>
        ) : null}
      </CardHeader>
      {/*
        The table keeps `<main>`'s gutter and its own frame on a phone. Run to
        the screen's edges, its first and last columns had no side space.
      */}
      <CardContent className={cn('grid gap-4', PHONE_FLUSH)}>
        {children}
      </CardContent>
    </Card>
  )
}
